// App.js - Updated with customer booking URL support
import React, { useState } from 'react';
import { Dashboard } from './pages/Dashboard';
import { Schedule } from './pages/Schedule';
import { Customers } from './pages/Customers';
import { CustomerDetail } from './pages/CustomerDetail';
import { Services } from './pages/Services';
import { CrewMembers } from './pages/CrewMembers';
import { Automations } from './pages/Automations';
import { CampaignsList } from './components/campaigns/CampaignsList';
import { Insights } from './pages/Insights';
import AppointmentDemo from './pages/CustomerSchedule/AppointmentDemo';
import CustomerBookingPage from './pages/CustomerSchedule/CustomerBookingPage';
import { useAuth } from './context/AuthContext';
import { BusinessConfigProvider, useBusinessConfig } from './context/BusinessConfigContext';
import Login from './pages/Login';
import RequestAccess from './pages/RequestAccess';
import InvoiceManagement from './pages/InvoiceManagement';
import { AdminPanel } from './pages/AdminPanel';
import { TemplatesAdmin } from './pages/TemplatesAdmin';
import { IncomingMessages } from './pages/IncomingMessages';
import { UserSessionProvider } from './context/UserSessionContext';
import { Journal } from './pages/Journal';
import { JournalProvider } from './components/doctorsJournal/JournalContext';
import { CustomerProvider } from './context/CustomerContext';

import "@fontsource/assistant";
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  const { user, isAuthorized, loading: authLoading, authorizationLoading } = useAuth();
  const { loading: configLoading } = useBusinessConfig();

  // Handle URL-based customer booking routing
  React.useEffect(() => {
    const path = window.location.pathname;
    const bookingMatch = path.match(/^\/book\/([^\/]+)$/);

    if (bookingMatch) {
      const businessSlug = bookingMatch[1];
      setCurrentPage('customer-booking');
      setSelectedCustomerId(businessSlug); // Reuse this state for businessSlug
    }
  }, []);

  const handleNavigate = (page, customerId = null) => {
    setCurrentPage(page);

    // Handle customer detail navigation
    if (page === 'customer-detail' && customerId) {
      setSelectedCustomerId(customerId);
    } else if (page !== 'customer-detail') {
      // Clear selected customer when navigating away from customer detail
      setSelectedCustomerId(null);
    }
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} />;
      case 'schedule':
        return <Schedule onNavigate={handleNavigate} />;
      case 'insights':
        return <Insights onNavigate={handleNavigate} />;
      case 'crew-members':
        return <CrewMembers onNavigate={handleNavigate} />;
      case 'customers':
        return <Customers onNavigate={handleNavigate} />;
      case 'customer-detail':
        return (
          <CustomerDetail
            onNavigate={handleNavigate}
            customerId={selectedCustomerId}
          />
        );
      case 'journal':
        return <Journal onNavigate={handleNavigate} />;
      case 'services':
        return <Services onNavigate={handleNavigate} />;
      case 'invoices':
        return <InvoiceManagement onNavigate={handleNavigate} />;
      case 'automations':
        return <Automations onNavigate={handleNavigate} />;
      case 'campaigns':
        return <CampaignsList onNavigate={handleNavigate} />;
      case 'customer-booking':
        return <CustomerBookingPage businessSlug={selectedCustomerId} />;
      case 'admin':
        return <AdminPanel onNavigate={handleNavigate} />;
      case 'templates-admin':
        return <TemplatesAdmin onNavigate={handleNavigate} />;
      case 'incoming-messages':
        return <IncomingMessages onNavigate={handleNavigate} />;
      default:
        return <Dashboard onNavigate={handleNavigate} />;
    }
  };

  if (authLoading || authorizationLoading || configLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        {/* Spinning logo/icon */}
        <div className="relative mb-6">
          {/* <div className="w-16 h-16 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin"></div> */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-pink-500 rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Loading text */}
        <div className="text-xl font-medium text-gray-700 mb-4">המערכת טוענת נתונים...</div>

        {/* Animated dots */}
        <div className="flex mt-6">
          <div className="w-2 h-2 ml-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 ml-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 ml-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    );
  }

  // For customer booking pages, skip authentication
  if (currentPage === 'customer-booking') {
    return (
      <div className="App">
        {renderCurrentPage()}
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  // Show unauthorized page if user is authenticated but not authorized
  if (user && isAuthorized === false) {
    return <RequestAccess />;
  }

  // Show loading while checking authorization
  if (user && isAuthorized === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Checking access permissions...</div>
      </div>
    );
  }

  return (
    <JournalProvider>
      <UserSessionProvider>
        <BusinessConfigProvider>
          <CustomerProvider>
            <div className="App">
              {renderCurrentPage()}
            </div>
          </CustomerProvider>
        </BusinessConfigProvider>
      </UserSessionProvider>
    </JournalProvider>
  );
}

export default App;